
import { Options, Vue } from "vue-class-component";
import { formatDate } from "@/app/infrastructures/misc/Utils";
import { mapStatus, mappingListPriority } from "../utils/status-mapping";
import HistorySection from "./history-section.vue";
import SummarySection from "./summary-section.vue";
import { PusatResolusiV2Controller } from '@/app/ui/controllers/PusatResolusiV2Controller';
import { TicketDetail, TicketAction, Conversation } from '@/domain/entities/PusatResolusiV2';
import { CLIENT_ERROR, INTERNET_ERROR, INTERNAL_SERVER_ERROR } from '@/app/infrastructures/misc/Utils';import { MainAppController } from '@/app/ui/controllers/MainAppController';
import { ModalMessageEntities } from '@/domain/entities/MainApp';
import { PusatResolusiV2Mapper } from '@/data/persistences/mappers/PusatResolusiV2Mapper';
import { AccountController } from '@/app/ui/controllers/AccountController';

@Options({
  components: {
    HistorySection,
    SummarySection
  }
})
export default class PusatResolusiDetailKendala extends Vue {
  CLIENT_ERROR = CLIENT_ERROR;
  INTERNAL_SERVER_ERROR = INTERNAL_SERVER_ERROR;
  INTERNET_ERROR = INTERNET_ERROR;

  ticketDetail = new TicketDetail();
  ticketAction = new TicketAction();
  loading = false;
  errorState = "";
  websocket: WebSocket | null = null;
  isCloseFromUnmounted = false;
  reconnectInterval: NodeJS.Timeout | null = null;
  websocketErrorModalEntities = new ModalMessageEntities({
    image: "error-token-ngen",
    imageWidth: "32",
    customWidth: "417px",
    title: "Terjadi gangguan sistem",
    message: `Klik "Muat ulang" atau coba laporkan beberapa saat lagi.`,
  });
  showWebsocketErrorModal = false;
  
  mounted() {
    this.getTicketDetail();
    this.connectWebsocket();
  }

   beforeUnmount() {
    this.isCloseFromUnmounted = true;
    this.closeWebsocketConnection();
    MainAppController.closeMessageModal();
  }

  get id() {
    return this.$route.params.id;
  }

  get title() {
    return this.ticketDetail.ticket.category.label || "";
  }
  goBack() {
    this.$router.push({
      name: "pusat-resolusi-v2-list"
    });
  }
  mapStatus = mapStatus;
  mappingListPriority = mappingListPriority;
  get currentStatus() {
    return mapStatus(this.ticketDetail.ticket.status);
  }
  get currentPriority() {
    return mappingListPriority(this.ticketDetail.ticket.priority.toUpperCase());
  }
  formatCreatedDate(date: any): string {
    return formatDate(date);
  }

  async getTicketDetail() {
    try {
      this.errorState = "";
      this.loading = true;
      this.ticketDetail = await PusatResolusiV2Controller.getTicketDetail(Number(this.$route.params.id as string));
      this.ticketAction = await PusatResolusiV2Controller.getTicketAction(Number(this.$route.params.id as string));
    } catch(err) {
      this._handleErrorGetTicketDetail(err);
    } finally {
      this.loading = false;
    }
  }

  _handleErrorGetTicketDetail(error: any) {
    if (!error.response) {
      this.errorState = INTERNET_ERROR;
      return;
    }
    if (error.response.status > 500) {
      this.errorState = CLIENT_ERROR;
      return;
    }

    this.errorState = INTERNAL_SERVER_ERROR;
  }

  async connectWebsocket() {
    this.websocket = await PusatResolusiV2Controller
      .connectConversationWebsocket(Number(this.$route.params.id as string)) as WebSocket;
    this.websocket.addEventListener("open", () => {
      clearInterval(this.reconnectInterval as NodeJS.Timeout);
      this.reconnectInterval = null;
    })

    this.websocket.addEventListener("message", async (event) => {
      const mapper = new PusatResolusiV2Mapper();
      const conversation = mapper.convertHistoryChatResponseFromWebsocket(event.data)[0];
      if (conversation.error) {
        this.showWebsocketErrorModal = true;
        return;
      }

      if (!conversation.type) {
        this.ticketAction = new TicketAction({
          isOpenMultipleTab: conversation.isOpenMultipleTab,
          isAssignedToOther: conversation.isAssignedToOther,
          assignedUsername: conversation.assignedUsername,
          action: conversation.action
        })
        this.handleCloseWebsocketPopup(conversation);
        const currentUsername = AccountController.accountData.username;
        if (conversation.isOpenMultipleTab)
          return this.showWebsocketPopup(
            "other_tab",
            conversation.isEscalatePartner
          );
        if (conversation.isAssignedToOther && conversation.assignedUsername !== currentUsername) return this.showWebsocketPopup();
      }

      this.websocketAction(conversation);
    })

    this.websocket.addEventListener("close", () => {
      if (this.reconnectInterval) return;
      if (this.isCloseFromUnmounted) return 
      this.reconnectInterval = setInterval(async () => {
        this.connectWebsocket();
      }, 4000)
    })
  }

  websocketAction(conversation: Conversation) {
    if (!conversation.type) return;
    const obj = {
      CS_AGENT_CHANGE: () => {
        this.ticketDetail.ticket.agentCsFullname = conversation.value.newAgent;
      },
      PRIORITY_CHANGE: () => {
        this.ticketDetail.ticket.priority = conversation.value.newPriority || "";
      },
      STATUS_CHANGE: async () => {
        this.ticketAction = await PusatResolusiV2Controller.getTicketAction(Number(this.$route.params.id as string));
        this.ticketDetail.ticket.status = conversation.value.status; 
      },
      CATEGORY_CHANGE: () => {
        this.ticketDetail.ticket.category.label = conversation.value.newCategory;
      },
      ESCALATION_NOTE: () => {
        this.getTicketDetail();
      },
      ESCALATE_DONE: () => {
        this.getTicketDetail();
      },
    } as any

    if (!obj[conversation.type]) return;
    obj[conversation.type]();

  }

  reconnectWebsocket() {
    this.showWebsocketErrorModal = false;
    this.closeWebsocketConnection();
    this.connectWebsocket();
  }

  showWebsocketPopup(type = "already_escalated", isEscalatePartner = false) {
    let message = `
<div class="flex flex-col gap-2">
  <div class="text-gray-lp-300 text-sm font-semibold ">Tiket ${
    isEscalatePartner ? "eskalasi " : ""
  }Anda sedang dibuka di tab lain.</div>
  ${
    isEscalatePartner
      ? '<div class="text-xs text-gray-lp-800">Lanjutkan pengerjaan tiket di tab atau browser tersebut.</div>'
      : ""
  }
</div>
`
    if (type === "already_escalated") {
      message = `
<div class="flex flex-col gap-2">
  <div class="text-gray-lp-300 text-sm font-semibold ">Tiket sudah dieskalasi oleh ${this.ticketAction.assignedUsername || ''}</div>
  <div class="text-xs text-gray-lp-800">Anda masih bisa eskalasi tiket dari kendala lainnya.</div>
</div>
`
    }
    this.isCloseFromUnmounted = true;
    this.closeWebsocketConnection();
      
    MainAppController.showMessageModal(new ModalMessageEntities({
      image: "ill_badge_claim_confirmation",
      imageWidth: "32",
      message: message,
      textSuccess: "Oke, mengerti",
      customWidth: "417px",
      idButtonSubmit: "button_confirm_already_assigned",
      onSubmit: () => {
        MainAppController.closeMessageModal();
        this.goBack();
      }
    }))
  }

  updateAction(action: TicketAction) {
    this.ticketAction = action;
  }

  handleCloseWebsocketPopup(conversation: Conversation) {
    if (!conversation.isAssignedToOther || !conversation.isOpenMultipleTab) MainAppController.closeMessageModal();
  }

  closeWebsocketConnection() {
    if (!this.websocket) return;
    this.websocket.close();
    this.websocket = null;
    clearInterval(this.reconnectInterval as NodeJS.Timeout);
  }
}
